/**
 * LeadFormEmbed — registry component for the marker `<div data-shiftgears="form">` (#1081).
 *
 * NOTE (#1161): renamed from `FormWidget` to disambiguate from the in-chat
 * `components/widgets/FormWidget.jsx` (rendered by WidgetRenderer from a
 * `{type:'form'}` agent payload). This is the standalone PAGE-EMBED form; the
 * `data-shiftgears="form"` marker still maps here via bootstrap COMPONENT_LOADERS.
 *
 * The frontend layer of the "config-driven form widget + AI handover" initiative
 * (parent #1084; backend contract = #1082 / !1044). It generalises the fields of
 * `CheckAvailabilityModal` into a standalone, config-driven, INLINE form (no modal
 * `fixed inset-0` chrome) that a dealer embeds anywhere on their site, and then
 * morphs the same Shadow DOM region form→chat after capture (synthesis UX).
 *
 * ## Flow
 *
 * 1. Render the form (name required; phone/email optional; low-emphasis inline
 *    preferred-channel radio; message; config-declared custom fields → `extra`;
 *    hidden honeypot).
 * 2. On submit: **CRM-capture first** via `submitLeadFromForm()` →
 *    `POST /api/widget/lead-capture`. Success is gated on the CRM capture, NOT on
 *    the optional MODX POST (which is fire-and-forget and must never gate success —
 *    inverts the CarDetailPage ordering). Read `{leadId}` from the result.
 * 3. Show the persistent "saved as lead" banner immediately, then morph the form
 *    card IN PLACE into a lightweight INLINE chat (`FormWidgetChat`, #1158) — an
 *    in-flow view inside the same Shadow-DOM region, NOT the floating
 *    `VoiceWidgetConnected` overlay. The inline view drives the conversation with
 *    the standalone `useTextChat` transport and forwards the frozen launchContext
 *    VERBATIM as an auto-greeting (empty message + `form_submitted` context).
 * 4. **Graceful fallback (mandatory):** arm a bounded ~2.5s watchdog when the
 *    morph starts; the inline view calls back `onReplyStarted` the moment the
 *    agent's OWN reply begins. Because `useTextChat` filters WS frames by the
 *    outgoing messageId, that signal is messageId-correlated — a co-hosted
 *    floating chat's concurrent stream cannot satisfy it (F4, #1158). On timeout /
 *    WS failure → a calm confirmation referencing `preferredChannel` + a soft
 *    "Try the assistant again" button. Never a spinner-forever/error. The lead is
 *    already saved, so the degraded path loses nothing.
 *
 * Because this file lives under `components/**`, its co-located `FormWidget.css`
 * is inlined into the Shadow DOM by the `import.meta.glob` in `bootstrap.jsx`.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CheckCircle, CircleNotch as Loader2, ChatText as MessageSquare } from '@phosphor-icons/react';
import { useModalPortalRoot } from '../../context/PortalContext';
import {
  pushFormModalOpen,
  popFormModalOpen,
} from '../../utils/formModalSignal';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/bodyScrollLock';
import { ContactParamsProvider } from '../../context/ContactParamsContext';
import { DutyFreeProvider, useDutyFree } from '../../context/DutyFreeContext';
import { NotificationProvider } from '../../context/NotificationContext';
import { getTheme } from '../VoiceWidget';
import { Suggestions } from '../VoiceWidget/Suggestions';
import FormWidgetChat from './FormWidgetChat';
import PhoneField, { isValidPhoneValue } from './PhoneField';
import { config as legacyConfig } from '../../config/runtimeConfig';
import { submitLeadFromForm } from '../../services/api';
import {
  CORE_INPUT_KEYS,
  WALKTHROUGH_MEDIA,
  canBeUnknown,
  formatRangeValue,
  initialCustomValue,
  isChannelField,
  isWalkthroughActive,
  rangeConfig,
  unknownLabelOf,
  walkthroughSteps,
} from './leadFormFields';
import {
  clearLeadFormDraft,
  getLeadFormDraft,
  newDraftId,
  sameContact,
  setLeadFormDraft,
} from '../../services/leadFormDraft';
import { ConsentCheckbox } from '../ui/ConsentCheckbox';
import { buildLeadConsents, useConsentLocale } from '../../utils/leadConsent';
import { getCurrentSessionId, getCurrentVisitorId } from '../../services/sessionManager';
import { trackEvent } from '../../services/analyticsBeacon';
import { trackLeadSubmitted } from '../../services/analyticsBridge';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './FormWidget.css';

/** Bounded wait for the agent's first reply before falling back (ms). */
const AGENT_REPLY_TIMEOUT_MS = 2500;

/**
 * How long the PPC success marker stays in the URL (ms, #1566). Long enough
 * for GTM's History Change trigger and the Meta pixel to see it once. A
 * navigation inside this window still re-fires a URL-based trigger — the
 * `lead_submitted` event has no such gap.
 */
const SUCCESS_MARKER_LIFETIME_MS = 1000;

/**
 * Счётчик инстансов формы — модульный, поэтому общий для ВСЕХ React-корней на
 * странице (виджет создаёт корень на каждый маркер). Нужен, чтобы id формы был
 * уникален независимо от количества корней: `useId` считает от нуля в каждом из
 * них. Подробнее — у объявления `formId` (#1270).
 */
let formIdSeq = 0;

/** Core fields the form can render (config picks a subset via formFields). */
const DEFAULT_FORM_FIELDS = ['name', 'phone', 'email', 'preferredChannel', 'message'];

/** Per-core-key default input type + required default. */
const CORE_FIELD_META = {
  name: { type: 'text', required: true },
  phone: { type: 'tel', required: false },
  email: { type: 'email', required: false },
  message: { type: 'textarea', required: false },
};

/**
 * Human-readable default labels for the core/channel field keys, used when a
 * field carries no explicit `label`. Renders as the persistent top `<label>`
 * above the control (owner-flagged: every field type gets a consistent label).
 */
const CORE_FIELD_LABELS = {
  name: 'Full name',
  phone: 'Phone',
  email: 'Email',
  message: 'Message',
  channel: 'Preferred contact',
};

/**
 * Default light inline hints for core fields (#1200, item 4) — restores the
 * sample text lost when persistent labels were introduced (#1087). Used only
 * when a field carries no explicit `placeholder`. Phone hints the national
 * number (the dial code lives in the PhoneField picker).
 */
const CORE_FIELD_PLACEHOLDERS = {
  name: 'e.g. John Smith',
  phone: '99 123456',
  email: 'you@example.com',
  message: 'How can we help?',
};

/**
 * Turn a machine key into a human label as a last resort for custom fields
 * without an explicit `label` (e.g. `pickup_location` → "Pickup location").
 */
export function humanizeKey(key) {
  if (typeof key !== 'string' || key.length === 0) return '';
  const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Ad-tracking params stripped from the MODX `url` field (mirrors CarDetailPage). */
const AD_TRACKING_PARAMS = [
  'gad_source', 'gad_campaignid', 'gclid', 'fbclid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
];

/**
 * Build the `url` value for the MODX FormIt post — the current page URL with
 * ad-tracking params stripped (they belong in analytics, not the dealer's lead
 * email). Returns path+search+hash, matching the pre-#1087 CarDetailPage
 * behaviour. No-op (undefined) outside the browser.
 */
export function buildSourceUrl() {
  if (typeof window === 'undefined') return undefined;
  try {
    const u = new URL(window.location.href);
    AD_TRACKING_PARAMS.forEach((k) => u.searchParams.delete(k));
    return u.pathname + u.search + u.hash;
  } catch {
    return undefined;
  }
}

/**
 * Substitute the `{car}` token in a title/label with the current car title
 * (#1087, D4). When there is no car title, the token collapses to empty and a
 * dangling connective ("… for", "… of") is trimmed so the title reads cleanly
 * (e.g. "Check Availability for {car}" → "Check Availability"). Non-string /
 * token-free input is returned unchanged.
 */
export function interpolateCarTitle(str, carTitle) {
  if (typeof str !== 'string' || !str.includes('{car}')) return str;
  const replaced = str.replace(/\{car\}/g, carTitle ? String(carTitle) : '');
  return replaced
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/\s+(for|of|about)\s*$/i, '')
    .trim();
}

/**
 * Build the UNIFIED ordered field list (#1179, D5.1) that the renderer iterates.
 *
 * Two inputs, one output:
 * - `unifiedFields` — a full `WidgetFormField[]` (from a template / the D5 designer
 *   preview). Used verbatim (order = render order), normalizing `enabled`/`type`.
 * - otherwise the LEGACY attrs path: core `fields: string[]` + separate
 *   `customFields[]`. Core keys become `builtin` entries (the old `preferredChannel`
 *   key maps to the `channel`-type field, `enabled` following `showChannelChooser`
 *   so the pre-#1179 chooser gating is preserved), then customs are appended.
 *
 * @returns {Array<{key,label,type,required,enabled,builtin,options}>}
 */
export function buildFieldList({ fields, customFields, showChannelChooser, unifiedFields }) {
  if (Array.isArray(unifiedFields) && unifiedFields.length > 0) {
    return unifiedFields
      .filter((f) => f && typeof f.key === 'string')
      .map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type || (f.key === 'channel' ? 'channel' : 'text'),
        required: !!f.required,
        enabled: f.enabled !== false,
        builtin: !!f.builtin,
        options: Array.isArray(f.options) ? f.options : undefined,
        // #1200 — per-field inline hint (item 4) + autosuggest pills (item 5).
        placeholder: typeof f.placeholder === 'string' ? f.placeholder : undefined,
        suggestions: Array.isArray(f.suggestions) ? f.suggestions : undefined,
        // #1227 — grid column width; only 'half' is meaningful (default full).
        width: f.width === 'half' ? 'half' : 'full',
        // #1469 — walk-through screen, slider bounds, "Don't know yet".
        step: Number.isInteger(f.step) && f.step > 0 ? f.step : undefined,
        min: typeof f.min === 'number' ? f.min : undefined,
        max: typeof f.max === 'number' ? f.max : undefined,
        increment: typeof f.increment === 'number' ? f.increment : undefined,
        defaultValue: typeof f.defaultValue === 'number' ? f.defaultValue : undefined,
        unit: typeof f.unit === 'string' && f.unit.trim() ? f.unit.trim() : undefined,
        unitPlural:
          typeof f.unitPlural === 'string' && f.unitPlural.trim() ? f.unitPlural.trim() : undefined,
        allowUnknown: f.allowUnknown === true,
        unknownLabel:
          typeof f.unknownLabel === 'string' && f.unknownLabel.trim()
            ? f.unknownLabel.trim()
            : undefined,
      }));
  }

  const list = [];
  for (const key of Array.isArray(fields) ? fields : []) {
    if (key === 'preferredChannel' || key === 'channel') {
      list.push({
        key: 'channel',
        type: 'channel',
        required: false,
        enabled: !!showChannelChooser,
        builtin: true,
      });
    } else if (CORE_FIELD_META[key]) {
      list.push({
        key,
        type: CORE_FIELD_META[key].type,
        required: CORE_FIELD_META[key].required,
        enabled: true,
        builtin: true,
      });
    }
  }
  for (const f of Array.isArray(customFields) ? customFields : []) {
    if (!f || !f.name) continue;
    list.push({
      key: f.name,
      label: f.label,
      type: f.type || 'text',
      required: !!f.required,
      enabled: true,
      builtin: false,
      options: Array.isArray(f.options) ? f.options : undefined,
      placeholder: typeof f.placeholder === 'string' ? f.placeholder : undefined,
      suggestions: Array.isArray(f.suggestions) ? f.suggestions : undefined,
    });
  }
  return list;
}

/** Custom (→ lead `extra`) fields: neither a core input nor the channel chooser. */
function customFieldsOf(fieldList) {
  return fieldList.filter(
    (f) => !CORE_INPUT_KEYS.includes(f.key) && !isChannelField(f)
  );
}

/**
 * Map the widget's UI channel value to the backend PreferredChannel enum.
 * Radio labels are Call / WhatsApp / Email → the backend accepts
 * 'phone' | 'email' | 'whatsapp'. "Call" is the human label for the 'phone'
 * channel (#1082 frozen contract).
 *
 * @param {string} uiValue
 * @returns {'phone' | 'email' | 'whatsapp' | undefined}
 */
export function mapPreferredChannel(uiValue) {
  switch (uiValue) {
    case 'call':
    case 'phone':
      return 'phone';
    case 'whatsapp':
      return 'whatsapp';
    case 'email':
      return 'email';
    default:
      return undefined;
  }
}

/** Human label for a backend PreferredChannel — used in the degraded confirmation. */
export function channelLabel(channel) {
  switch (channel) {
    case 'phone':
      return 'by phone';
    case 'whatsapp':
      return 'on WhatsApp';
    case 'email':
      return 'by email';
    default:
      return 'shortly';
  }
}

/**
 * Resolve the effective form config from per-embed marker `data-*` attrs, falling
 * back to the whitelisted globals (`window.b2bMountParams.form*`, surfaced by
 * runtimeConfig getters). Per-embed attrs win so one page can host several forms.
 *
 * @param {Object} attrs - parsed data-* attributes from the marker
 * @returns {Object} resolved config
 */
export function resolveFormConfig(attrs = {}) {
  const a = attrs || {};

  // ── Resolve-by-code (#1087, D4) ───────────────────────────────────────────
  // A caller may pass a template `code`; look it up in the runtime-threaded
  // `formTemplates` registry (built-in ⊕ org overrides, served by the public
  // widget-config API). The resolved template sits BETWEEN per-instance attrs
  // (win) and legacy/defaults (lose): each field below reads
  // `attr → template → legacy/default`. A per-embed `data-form-template-list`
  // attr can override the global registry (designer preview / tests).
  //
  // Attribute name reconciliation (#1225): two callers select a template by code
  // under DIFFERENT attr names, both accepted here:
  //   - `templateId` — the b2b-ui embed-snippet generator (#1123) emits
  //     `data-template-id` from the configured form embed row. This MUST be read
  //     or the admin-generated snippet silently falls back to the default form.
  //   - `formTemplateCode` / `formTemplate` / `code` — the in-SPA car page
  //     (CarDetailPage, #1087) passes `formTemplateCode` programmatically.
  // `templateId` is the canonical config-field name; the rest are kept so the
  // car-page path is untouched (full convergence tracked separately in #1225).
  const templateCode =
    a.templateId || a.formTemplateCode || a.formTemplate || a.code || null;
  const templateList =
    (Array.isArray(a.formTemplates) && a.formTemplates.length > 0
      ? a.formTemplates
      : null) ||
    legacyConfig.FORM_TEMPLATES ||
    [];
  const template = templateCode
    ? templateList.find((t) => t && t.code === templateCode) || null
    : null;

  const channelsFromAttr = Array.isArray(a.formPreferredChannels)
    ? a.formPreferredChannels
    : null;
  const preferredChannels =
    channelsFromAttr ||
    (template && Array.isArray(template.preferredChannels)
      ? template.preferredChannels
      : null) ||
    legacyConfig.FORM_PREFERRED_CHANNELS ||
    ['phone', 'whatsapp', 'email'];

  const fields =
    (Array.isArray(a.formFields) && a.formFields.length > 0 ? a.formFields : null) ||
    legacyConfig.FORM_FIELDS ||
    DEFAULT_FORM_FIELDS;

  const customFields =
    (Array.isArray(a.formCustomFields) && a.formCustomFields.length > 0
      ? a.formCustomFields
      : null) ||
    legacyConfig.FORM_CUSTOM_FIELDS ||
    [];

  const ALLOWED_FORM_TYPES = [
    'contact',
    'enquiry',
    'check_availability',
    'finance',
    'test_drive',
    'leasing_booking',
  ];
  const attrFormType = ALLOWED_FORM_TYPES.includes(a.formType) ? a.formType : null;
  const templateFormType =
    template && ALLOWED_FORM_TYPES.includes(template.formType) ? template.formType : null;
  const formType = attrFormType || templateFormType || legacyConfig.FORM_TYPE || 'contact';

  // Show the channel chooser only when it is enabled AND at least one channel
  // is offered AND the form declares the preferredChannel field. (For the
  // unified fieldList render path the channel field's own `enabled` flag is the
  // authority; this cfg flag stays for the legacy path + backward-compat.)
  const showChannelChooserPref =
    a.formShowChannelChooser !== undefined
      ? a.formShowChannelChooser === true || a.formShowChannelChooser === 'true'
      : template && typeof template.showChannelChooser === 'boolean'
        ? template.showChannelChooser
        : legacyConfig.FORM_SHOW_CHANNEL_CHOOSER;
  const showChannelChooser =
    showChannelChooserPref &&
    preferredChannels.length > 0 &&
    fields.includes('preferredChannel');

  const defaultChannelRaw =
    a.formDefaultChannel ||
    (template && template.defaultChannel) ||
    legacyConfig.FORM_DEFAULT_CHANNEL ||
    'phone';
  // Ensure the preselected channel is actually offered.
  const defaultChannel = preferredChannels.includes(defaultChannelRaw)
    ? defaultChannelRaw
    : preferredChannels[0] || 'phone';

  const morphToChat =
    a.formMorphToChat !== undefined
      ? !(a.formMorphToChat === false || a.formMorphToChat === 'false')
      : template && typeof template.morphToChat === 'boolean'
        ? template.morphToChat
        : legacyConfig.FORM_MORPH_TO_CHAT;

  // Unified ordered field list (#1179). Priority: per-embed `formFieldList` →
  // the resolved template's `fields` (#1087) → legacy global → legacy
  // `fields`+`customFields`. Order = render order.
  const unifiedFields =
    (Array.isArray(a.formFieldList) && a.formFieldList.length > 0
      ? a.formFieldList
      : null) ||
    (template && Array.isArray(template.fields) && template.fields.length > 0
      ? template.fields
      : null) ||
    legacyConfig.FORM_FIELD_LIST ||
    null;
  const fieldList = buildFieldList({
    fields,
    customFields,
    showChannelChooser,
    unifiedFields,
  });

  // Appearance overrides (#1179). Omitted → inherits the widget theme.
  const appearance =
    (a.formAppearance && typeof a.formAppearance === 'object'
      ? a.formAppearance
      : null) ||
    (template && template.appearance && typeof template.appearance === 'object'
      ? template.appearance
      : null) ||
    legacyConfig.FORM_APPEARANCE ||
    null;

  // Presentation: per-embed override (`presentation` / `formPresentation` attr)
  // beats the template's `appearance.presentation` default, which beats 'inline'.
  const presentationOf = (v) => (v === 'modal' || v === 'inline' ? v : null);
  const presentation =
    presentationOf(a.presentation) ||
    presentationOf(a.formPresentation) ||
    presentationOf(appearance && appearance.presentation) ||
    'inline';

  // Modal auto-open opt-in (review !1113 #1). A real `presentation:'modal'` embed
  // must default CLOSED (show the "Open form" trigger) — auto-opening hijacks the
  // whole host page on load. ONLY an explicit opt-in auto-opens on mount; the D5.4
  // designer preview passes it so the form is visible without a click. There is
  // deliberately NO global fallback here — real embeds never auto-open.
  const autoOpenModal = a.formModalOpen === true || a.formModalOpen === 'true';

  // Designer-preview-only phase override (D5.4). `form` (default) shows the form;
  // `after` shows the post-submit confirmation card WITHOUT a live capture (the
  // chat morph needs a live agent WS, so the preview shows the confirmation state
  // the "After submit" toggle represents). Never set on production embeds.
  const previewPhase = a.formPreviewPhase === 'after' ? 'after' : null;

  const carTitle = a.carTitle || a.vehicleInterest || null;

  // Title supports a `{car}` token (#1087, D4) substituted with the car title
  // (or trimmed away when absent). Priority: per-embed → template → legacy.
  const rawTitle =
    a.formTitle ||
    (template && template.title) ||
    legacyConfig.FORM_TITLE ||
    'Get in touch';

  return {
    title: interpolateCarTitle(rawTitle, carTitle),
    // Modal trigger button label (#1227). Priority: per-embed → template →
    // null (render falls back to `title`). Also `{car}`-interpolated so a
    // per-car CTA reads cleanly. Only consumed in modal presentation.
    ctaLabel: interpolateCarTitle(
      a.formCtaLabel || (template && template.ctaLabel) || null,
      carTitle,
    ),
    submitLabel:
      a.formSubmitLabel ||
      (template && template.submitLabel) ||
      legacyConfig.FORM_SUBMIT_LABEL ||
      'Send',
    // Generic form identity for the MODX `formEnquire` field (#1087): the
    // template's human label (e.g. "BF Finance Application"), overridable per
    // embed via `formLabel`. NOT the car-specific title.
    label: a.formLabel || (template && template.label) || null,
    // Template code that produced this cfg (diagnostics / analytics grouping).
    templateCode: templateCode || (template && template.code) || null,
    formType,
    fields,
    customFields,
    // Unified ordered field list (#1179) — the render source of truth.
    fieldList,
    appearance,
    presentation,
    autoOpenModal,
    previewPhase,
    preferredChannels,
    showChannelChooser,
    defaultChannel,
    successBanner:
      a.formSuccessBanner ||
      (template && template.successBanner) ||
      legacyConfig.FORM_SUCCESS_BANNER ||
      "Thanks — we've saved your request and a specialist will follow up.",
    morphToChat,
    handoverCtaText:
      a.formHandoverCtaText ||
      (template && template.handoverCtaText) ||
      legacyConfig.FORM_HANDOVER_CTA_TEXT ||
      'Try the assistant',
    // MODX endpoint: per-embed attr wins, then the template's configured
    // endpoint (#1087). The per-org car-page endpoint was removed (#1214), so
    // for code-defined car-page forms this resolves from a form template's own
    // `modxEndpoint` if any, else null (CRM capture only).
    modxEndpoint: a.formModxEndpoint || (template && template.modxEndpoint) || null,
    // VERBATIM MODX field passthrough (#1087 owner decision). When provided the
    // MODX post uses THIS map EXACTLY — the generic `{formEnquire,…,…extra}`
    // shape is NOT merged over it. Lets the car page reproduce Andys' EXACT
    // legacy FormIt payload (snake_case `finance_amount` / `enquiry_type`, the
    // `€…` formatting, the `formEnquire` literals) with zero notification risk,
    // while the CRM lead `extra` stays clean camelCase. Either a plain object
    // (static) OR a builder `(ctx) => fieldMap` invoked at submit with
    // `{ formData, extra, carTitle, sourceUrl }` (so it can read the values the
    // visitor just typed). Omitted → the generic default post is used.
    modxFields:
      typeof a.formModxFields === 'function' ||
      (a.formModxFields && typeof a.formModxFields === 'object' && !Array.isArray(a.formModxFields))
        ? a.formModxFields
        : null,
    // #1162 — PPC conversion marker appended to the page URL on a successful
    // submit. A single `key=value` string (default `form=success`).
    successParam:
      a.formSuccessParam ||
      (template && template.successParam) ||
      legacyConfig.FORM_SUCCESS_PARAM ||
      'form=success',
    // Non-rendered lead metadata (#1087): the caller attaches finance figures
    // (carPrice / deposit / financeAmount, …) that flow to the lead `extra` and
    // through the MODX post via `...extra`. Never shown as an input.
    extraData:
      a.formExtra && typeof a.formExtra === 'object' && !Array.isArray(a.formExtra)
        ? a.formExtra
        : null,
    carId: a.carId || null,
    carTitle,
  };
}

/**
 * Parse a `key=value` marker string (e.g. `form=success`) into its parts.
 * Splits on the FIRST `=` so a value may itself contain `=`. Returns null when
 * the key is empty / input is not a usable string.
 *
 * @param {string} raw
 * @returns {{ key: string, value: string } | null}
 */
export function parseSuccessParam(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const eq = trimmed.indexOf('=');
  const key = (eq === -1 ? trimmed : trimmed.slice(0, eq)).trim();
  const value = eq === -1 ? '' : trimmed.slice(eq + 1).trim();
  if (!key) return null;
  return { key, value };
}

/**
 * On a successful capture, append the configured PPC-conversion marker to the
 * CURRENT page URL WITHOUT reloading (#1162), via the History API.
 *
 * Preserves ALL existing query params (widget-view, activation params,
 * utm/gclid, …): we clone the current URL and `searchParams.set` the single
 * configured key, so nothing else is clobbered and the hash is kept intact. A
 * full navigation/reload would kill the inline form→chat morph, so we use
 * `history.replaceState` (no navigation). No-op outside the browser.
 *
 * @param {string} paramString - `key=value` (default `form=success`)
 */
export function emitSuccessUrlMarker(paramString) {
  if (typeof window === 'undefined' || !window.history || !window.location) return;
  const parsed = parseSuccessParam(paramString);
  if (!parsed) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(parsed.key, parsed.value);
    // Keep the entry's state: the widget's HashRouter stores idx/usr there.
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (e) {
    console.warn('[LeadFormEmbed] Failed to emit success URL marker (non-blocking):', e);
  }
}

/**
 * Take the PPC marker back out of the URL (#1566). While it stays there, GTM's
 * History Change trigger re-fires the lead tag on EVERY later URL change (the
 * widget's hash routing, chat restores, …) — one visitor counted as 17 leads.
 * Only removes the key while it still holds our value; everything else in the
 * URL and the current history state is kept.
 *
 * @param {string} paramString - `key=value` (default `form=success`)
 */
export function clearSuccessUrlMarker(paramString) {
  if (typeof window === 'undefined' || !window.history || !window.location) return;
  const parsed = parseSuccessParam(paramString);
  if (!parsed) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(parsed.key) !== parsed.value) return;
    url.searchParams.delete(parsed.key);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (e) {
    console.warn('[LeadFormEmbed] Failed to clear success URL marker (non-blocking):', e);
  }
}

/**
 * Build the frozen launchContext for the form→chat handover (#1081/#1084).
 *
 * Frozen shape:
 *   { action: 'form_submitted', leadAlreadyCaptured: true,
 *     metadata: { carId, carTitle, lead: {...}, extra: { estimatedMonthly? } } }
 * `carId` stays FLAT inside metadata (backward-compat with the existing
 * `{action, metadata:{carId, carTitle}}` shape); we ADD the `lead` + `extra` blocks.
 *
 * @param {Object} params
 * @returns {Object} launchContext
 */
export function buildLaunchContext({ lead, cfg, estimatedMonthly }) {
  const extra = {};
  if (typeof estimatedMonthly === 'number' && estimatedMonthly > 0) {
    extra.estimatedMonthly = estimatedMonthly;
  }
  return {
    action: 'form_submitted',
    leadAlreadyCaptured: true,
    metadata: {
      carId: cfg.carId || undefined,
      carTitle: cfg.carTitle || undefined,
      lead: {
        leadId: lead.leadId || null,
        name: lead.name,
        phone: lead.phone || undefined,
        email: lead.email || undefined,
        message: lead.message || undefined,
        formType: lead.formType,
        preferredChannel: lead.preferredChannel || undefined,
      },
      extra,
    },
  };
}

/**
 * Fire the optional MODX FormIt POST. Fire-and-forget: it MUST NOT gate success
 * and MUST NOT throw into the caller (inverts today's CarDetailPage ordering,
 * where the MODX iframe POST was the gating await). Uses the same hidden
 * iframe + form pattern as CarDetailPage to bypass CORS.
 */
function fireModxPost(endpoint, fields) {
  if (!endpoint || typeof document === 'undefined') return;
  let parsed;
  try {
    parsed = new URL(endpoint);
  } catch {
    console.warn('[LeadFormEmbed] Invalid MODX endpoint, skipping:', endpoint);
    return;
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) return;

  try {
    const iframeName = 'sg-form-modx-' + Date.now();
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = endpoint;
    form.target = iframeName;

    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined || value === null) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    }
    document.body.appendChild(form);

    const cleanup = () => setTimeout(() => {
      iframe.remove();
      form.remove();
    }, 1000);

    form.submit();
    iframe.addEventListener('load', cleanup);
    iframe.addEventListener('error', cleanup);
    // Safety net: never leak DOM if neither event fires.
    setTimeout(cleanup, 15000);
  } catch (e) {
    console.warn('[LeadFormEmbed] MODX POST failed (non-blocking):', e);
  }
}

/**
 * LeadFormEmbedInner — the form / morph state machine (form → saving → chat |
 * degraded). On capture it freezes the launchContext and renders the inline
 * `FormWidgetChat`, guarding the handover with a messageId-correlated watchdog.
 */
function LeadFormEmbedInner({ cfg, onClose }) {
  // Dealer-configured balloon-loan calculator (#1087). Reads the per-org BF
  // terms (installmentMonths / annualRate / balloon…) from the widget config —
  // NOT the bare default-term `calculateMonthlyPayment`. This fixes the finance
  // path that previously estimated the monthly with hardcoded default terms.
  const { calculateMonthlyPayment } = useDutyFree();
  // 'form' → 'saving' → 'chat' | 'degraded'. The designer preview may seed the
  // post-submit confirmation ('after') so the "After submit" toggle is faithful
  // without a live capture (D5.4).
  const [phase, setPhase] = useState(cfg.previewPhase === 'after' ? 'degraded' : 'form');
  const [savedLead, setSavedLead] = useState(
    cfg.previewPhase === 'after'
      ? { name: '', formType: cfg.formType, preferredChannel: cfg.defaultChannel }
      : null
  ); // { name, phone, email, message, formType, preferredChannel, leadId, estimatedMonthly }
  const [launchContext, setLaunchContext] = useState(null); // frozen form_submitted context for the inline chat
  const [formError, setFormError] = useState(null);
  // #1200 — per-key phone validation errors (filled-but-invalid). Empty+required
  // is handled natively by the input's `required` attr.
  const [phoneErrors, setPhoneErrors] = useState({});

  // #1440 — the two contact-consent boxes, both unchecked by default: the
  // required one for this request (incl. the AI assistant), the optional one
  // for offers. Same locale for rendering and for the payload, so b2b-api
  // records the text the visitor read; dealer name = API widget-config company
  // name only (CONSENT_DEALER_NAME) — the same field b2b-api substitutes.
  const [consent, setConsent] = useState({ request: false, marketing: false });
  const consentLocale = useConsentLocale();

  const [formData, setFormData] = useState(() => ({
    name: '',
    phone: '',
    email: '',
    message: '',
    preferredChannel: cfg.defaultChannel,
    honeypot: '',
  }));
  // Custom (→ `extra`) fields, derived from the unified ordered list (#1179).
  const customFieldList = useMemo(() => customFieldsOf(cfg.fieldList), [cfg.fieldList]);
  const [customValues, setCustomValues] = useState(() => {
    const init = {};
    for (const f of customFieldList) init[f.key] = initialCustomValue(f);
    return init;
  });
  // #1469 — custom fields whose "Don't know yet" box is ticked, `{ [key]: true }`.
  const [unknownKeys, setUnknownKeys] = useState({});

  // Re-init custom values when the custom field SET changes under a mounted
  // widget (review !1113 #4). The useState initializer runs once, so a config
  // change (esp. the D5.4 designer preview live-editing fields) would leave new
  // custom fields uninitialized / stale keys lingering. Reconcile on every change
  // to the custom-key signature: keep existing values, seed new keys to '', drop
  // removed keys. The designer preview ALSO re-keys the iframe on structural
  // changes (belt-and-suspenders), but this keeps the component self-correcting.
  const customKeysSig = useMemo(
    () => customFieldList.map((f) => f.key).join('|'),
    [customFieldList]
  );
  useEffect(() => {
    setCustomValues((prev) => {
      const next = {};
      for (const f of customFieldList) next[f.key] = prev[f.key] ?? initialCustomValue(f);
      return next;
    });
    // customFieldList is derived from customKeysSig; depend on the sig so this
    // fires exactly when the key set changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customKeysSig]);

  // Modal presentation (#1179): a modal embed defaults CLOSED — it renders an
  // "Open form" trigger, and only the explicit `autoOpenModal` opt-in (the D5.4
  // designer preview) auto-opens on mount (review !1113 #1). Inline ignores this.
  const isModal = cfg.presentation === 'modal';
  const [modalOpen, setModalOpen] = useState(!!cfg.autoOpenModal);
  const sheetRef = useRef(null);

  // #1270 — id формы: кнопка отправки живёт ВНЕ `<form>` (в футере, который
  // модалка закрепляет снизу) и связана с ней атрибутом `form`. Иначе футер
  // пришлось бы держать внутри формы, а тогда его нельзя развязать с
  // grid-раскладкой полей — в grid `margin-top: auto` не толкает элемент вниз, а
  // переход формы на flex ломает поля в полширины (`width: 'half'`, #1227).
  //
  // Одного `useId` НЕ хватает (ревью #1270): виджет создаёт свой
  // `ReactDOM.createRoot` на каждый маркер (`bootstrap.jsx`), а `useId` считает
  // от нуля в каждом корне — две формы на странице получили бы один id. Сейчас
  // их спасает только то, что каждый эмбед в своём Shadow DOM (там своё
  // пространство id), но модалка портируется в ОБЩИЙ body-level portal (#1232),
  // и там дубль означал бы отправку чужой формы. Модульный счётчик общий для
  // всех корней, поэтому уникальность не зависит от их количества. Двоеточия из
  // `useId` убираем — с ними id невалиден как CSS-селектор.
  const reactId = useId();
  // Порядковый номер инстанса — через ленивый инициализатор, а не присваиванием
  // в рендере: виджет обёрнут в `React.StrictMode`, который в dev вызывает
  // функцию компонента дважды, и render-phase side effect пришлось бы каждый раз
  // доказывать читателю. Если инициализатор прогонится дважды, максимум что
  // будет — «съеденный» номер; уникальность не страдает (ревью #1270).
  const [seq] = useState(() => ++formIdSeq);
  const formId = `sg-form-${reactId.replace(/:/g, '')}-${seq}`;

  // #1232 — the modal renders into a body-level portal (not this embed's shadow
  // root) so its fixed backdrop escapes to the real viewport and its z-index
  // beats host chrome (a fixed navbar that otherwise traps the embed and hides
  // the sheet / its × close on mobile). Resolver is called only at open-time
  // (below), so inline-only forms never create the body node.
  const resolveModalPortalRoot = useModalPortalRoot();

  // #1232 item 3 — while this modal is open, signal the floating chat widget to
  // hide so it doesn't cover the full-screen form / its submit on mobile.
  //
  // #1261 — и одновременно ЛОЧИМ скролл страницы под модалкой. Замер на живой
  // странице дилера: при открытой модалке `window.scrollY` уезжал 0→600, т.е.
  // под пальцем было ДВА владельца скролла (лист формы + сама страница) — это и
  // читалось как «модалка скроллится вместе со страницей».
  //
  // Лок вынесен в `bodyScrollLock`: одного `body { overflow: hidden }` на iOS
  // Safari НЕ хватает (ревью #1261), нужен `position: fixed` со сдвигом на
  // текущий scroll и возвратом позиции при закрытии. Там же счётчик, поэтому
  // вложенный случай (форма поверх оверлея лендинга, #1256) не отпускает
  // страницу за внешний слой.
  useEffect(() => {
    if (!isModal || !modalOpen) return undefined;
    pushFormModalOpen();
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
      popFormModalOpen();
    };
  }, [isModal, modalOpen]);

  // Closing the modal collapses to the trigger AND notifies a controlling host
  // (#1087 — the car page owns the CTA button and unmounts on close, so it must
  // learn when the user dismisses the sheet). Inline embeds have no modal chrome
  // and never call this.
  //
  // #1469 — closing a walk-through mid-way is leaving it: the finished screens
  // go out as a partial lead. `leaveFormRef` is set further down, where that
  // machinery lives.
  const leaveFormRef = useRef(null);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    if (leaveFormRef.current) leaveFormRef.current();
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  // Widget theme — same source as registry/Chat.jsx.
  const widgetTheme = useMemo(
    () =>
      getTheme(legacyConfig.WIDGET_STYLE, legacyConfig.WIDGET_THEME_MODE, {
        accent: legacyConfig.WIDGET_ACCENT,
        glass: legacyConfig.WIDGET_GLASS,
      }),
    []
  );

  // Graceful-fallback watchdog: once we morph to chat, wait a bounded window for
  // the agent's OWN reply to start. On timeout we flip to the degraded
  // confirmation. The lead is already saved.
  //
  // #1158 F4 fix: the "reply started" signal no longer comes from a raw
  // `browserControlWS` subscription (which carried no messageId, so a co-hosted
  // floating chat's concurrent stream could falsely satisfy it). Instead the
  // inline `FormWidgetChat` calls `handleReplyStarted` from its `useTextChat`
  // callbacks, which fire ONLY for the morph's OWN outgoing greeting messageId.
  const replySeenRef = useRef(false);
  const watchdogRef = useRef(null);

  // #1121 — form-funnel analytics. `sg_form_view` / `sg_form_submit` MATCH the
  // event name + `{ formType, carId }` payload that #1116 emits from
  // CheckAvailabilityModal, so the two form surfaces share one funnel; the
  // `sg_form_handover_*` / `sg_form_degraded` events extend the same funnel for
  // the form→chat morph. `trackEvent` stamps visitorId + sessionId at flush and
  // no-ops before initBeacon().
  const analyticsFiredRef = useRef({ view: false, handoverComplete: false, degraded: false });
  // Current lead id for the active morph — read from the watchdog / reply
  // callbacks, which close over stale `savedLead` state otherwise.
  const activeLeadIdRef = useRef(null);
  // #1162 — emit the PPC conversion URL marker at most once per submit.
  const successMarkerFiredRef = useRef(false);
  // #1566 — the marker's pending removal, `{ param, timer }` while it is in the
  // URL. Unmounting inside that window removes it at once: it must not outlive
  // the form, or every later URL change re-fires the lead tag.
  const successMarkerClearRef = useRef(null);
  useEffect(
    () => () => {
      const pending = successMarkerClearRef.current;
      if (!pending) return;
      successMarkerClearRef.current = null;
      clearTimeout(pending.timer);
      clearSuccessUrlMarker(pending.param);
    },
    []
  );
  const trackForm = useCallback(
    (eventName, extra = {}) => {
      trackEvent(eventName, { formType: cfg.formType, carId: cfg.carId, ...extra });
    },
    [cfg.formType, cfg.carId]
  );

  // sg_form_view — the form embed became visible (mounted). Guarded once per
  // lifecycle (React StrictMode double-invokes effects in dev).
  useEffect(() => {
    if (analyticsFiredRef.current.view) return;
    analyticsFiredRef.current.view = true;
    trackForm('sg_form_view');
  }, [trackForm]);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  useEffect(() => () => clearWatchdog(), [clearWatchdog]);

  // Modal a11y (review !1113 #2): when the sheet is open, move focus into it,
  // contain Tab within it (focus-trap), and close on Escape. Scoped to the sheet
  // element so it never interferes with an inline embed or a co-hosted widget.
  useEffect(() => {
    if (!isModal || !modalOpen) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;

    const FOCUSABLE =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // Focus the dialog container itself on open (standard dialog behavior);
    // Tab then advances into the fields.
    sheet.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(sheet.querySelectorAll(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === firstEl || active === sheet || !sheet.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (active === lastEl || !sheet.contains(active)) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    sheet.addEventListener('keydown', onKeyDown);
    return () => sheet.removeEventListener('keydown', onKeyDown);
  }, [isModal, modalOpen, closeModal]);

  const armReplyWatchdog = useCallback(() => {
    // Clear any prior timer (e.g. the degraded "try again" re-morph) so we never
    // stack timers across attempts.
    clearWatchdog();
    replySeenRef.current = false;

    watchdogRef.current = setTimeout(() => {
      const timedOut = !replySeenRef.current;
      clearWatchdog();
      if (timedOut) {
        // Mark this attempt resolved so a late reply frame cannot ALSO fire
        // sg_form_handover_complete — degrade is terminal for the attempt. In
        // practice FormWidgetChat unmounts on the phase flip below (tearing down
        // its onReplyStarted callback), but make the invariant explicit rather
        // than rely on that unmount ordering. A retry re-arms via armReplyWatchdog,
        // which resets this flag.
        replySeenRef.current = true;
        console.warn('[LeadFormEmbed] Agent reply did not start within timeout — degraded confirmation');
        // sg_form_degraded — the handover fell back (agent never replied). Guarded
        // once per lifecycle. Only the watchdog-timeout path is a real degrade;
        // the honeypot / morphToChat=false paths reuse the 'degraded' phase but
        // are not handover failures, so they intentionally do NOT emit this.
        if (!analyticsFiredRef.current.degraded) {
          analyticsFiredRef.current.degraded = true;
          trackForm('sg_form_degraded', { leadId: activeLeadIdRef.current || undefined });
        }
        setPhase('degraded');
      }
    }, AGENT_REPLY_TIMEOUT_MS);
  }, [clearWatchdog, trackForm]);

  // Called (messageId-correlated) by the inline chat the moment the agent's own
  // reply begins → disarm the degraded watchdog.
  const handleReplyStarted = useCallback(() => {
    if (replySeenRef.current) return;
    replySeenRef.current = true;
    clearWatchdog();
    // sg_form_handover_complete — the agent's own reply/greeting started. Guarded
    // once per lifecycle (replySeenRef resets per morph attempt, so use a
    // dedicated flag to never double-count on a retry).
    if (!analyticsFiredRef.current.handoverComplete) {
      analyticsFiredRef.current.handoverComplete = true;
      trackForm('sg_form_handover_complete', { leadId: activeLeadIdRef.current || undefined });
    }
  }, [clearWatchdog, trackForm]);

  // ─── #1469 — multi-screen walk-through ────────────────────────────────────
  // Fields carry a `step`; on narrow screens (or always, per template) the form
  // shows one step per screen. Only the current step's controls are in the DOM,
  // so the browser's own `required` validation on submit covers exactly that
  // screen, and the primary button is "Next" until the last one.
  const steps = useMemo(() => walkthroughSteps(cfg.fieldList), [cfg.fieldList]);
  const isNarrow = useMediaQuery(WALKTHROUGH_MEDIA);
  const walkthrough = isWalkthroughActive({
    steps,
    mode: cfg.appearance && cfg.appearance.walkthrough,
    isNarrow,
  });
  const [stepIndex, setStepIndex] = useState(0);
  const safeStepIndex = Math.min(stepIndex, Math.max(0, steps.length - 1));
  const currentStep = walkthrough ? steps[safeStepIndex] : null;
  const isLastStep = !walkthrough || safeStepIndex >= steps.length - 1;
  const stepTitleRef = useRef(null);
  const stepMovedRef = useRef(false);

  // Screen change → announce + move focus to the step heading (also scrolls the
  // sheet back up), and count the step view for the funnel. Skipped on mount so
  // opening the form doesn't steal focus from the first field.
  // A step counts as viewed only while it's on screen: a CLOSED modal form (the
  // "Request an Offer" trigger on the leasing pages / landing) shows no step —
  // counting on mount logged a step-1 view on every page load.
  const stepsOnScreen = !isModal || modalOpen;
  useEffect(() => {
    if (!walkthrough || !stepsOnScreen) return;
    trackForm('sg_form_step_view', { step: safeStepIndex + 1, steps: steps.length });
    if (!stepMovedRef.current) return;
    stepMovedRef.current = false;
    if (stepTitleRef.current) stepTitleRef.current.focus();
  }, [walkthrough, stepsOnScreen, safeStepIndex, steps.length, trackForm]);

  const goToStep = (index) => {
    stepMovedRef.current = true;
    setFormError(null);
    setStepIndex(Math.max(0, Math.min(index, steps.length - 1)));
  };

  // ─── #1469 — partial leads ────────────────────────────────────────────────
  // Every "Next" records the finished screens (services/leadFormDraft, memory
  // only). If the visitor leaves mid-way — closes the form, switches apps,
  // closes the tab — those screens go to the CRM as a partial lead; if they
  // finish, the full form goes as before and completes that same lead (same
  // draft id).
  const draftKey = `${cfg.formType}:${cfg.templateCode || 'default'}`;
  // Picks up a draft from an earlier mount on this page too (the landing
  // unmounts the form on close), but `draftFor` continues a draft only for
  // the same phone or email.
  const [earlierDraft] = useState(() => getLeadFormDraft(draftKey));
  const draftRef = useRef(earlierDraft);
  const inFlightRevRef = useRef(0);
  const partialSendRef = useRef(null);
  // The full form is on its way — no more partials.
  const finishingRef = useRef(false);

  // The first screen with a phone or email: from there on a partial lead has
  // a contact to call back, which the save note says.
  const contactStep = useMemo(() => {
    const contactSteps = cfg.fieldList
      .filter((f) => f.enabled !== false && (f.key === 'phone' || f.key === 'email'))
      .map((f) => f.step || 1);
    return contactSteps.length > 0 ? Math.min(...contactSteps) : steps[0] || 1;
  }, [cfg.fieldList, steps]);
  const onContactScreen = !walkthrough || currentStep === contactStep;
  const onFirstScreen = !walkthrough || safeStepIndex === 0;

  // Send the finished screens the CRM hasn't had yet. `keepalive` lets the
  // request outlive a page that's closing.
  const sendPartial = useCallback(
    (keepalive = false) => {
      const draft = draftRef.current;
      if (!draft || finishingRef.current) return;
      const { rev, payload } = draft;
      if (draft.sentRev >= rev || inFlightRevRef.current >= rev) return;
      inFlightRevRef.current = rev;
      const { progress } = payload;
      trackForm('sg_form_partial', { step: progress.completedSteps, steps: progress.totalSteps });
      partialSendRef.current = Promise.resolve(submitLeadFromForm(payload, { keepalive }))
        // Some browsers refuse a page-outliving request that needs a CORS
        // preflight (ours does: cross-origin, token header). The page is
        // usually still alive here — the visitor switched apps or locked the
        // phone — so send it again the ordinary way.
        .then((result) => (result || !keepalive ? result : submitLeadFromForm(payload)))
        .then((result) => {
          if (result) {
            draft.sentRev = Math.max(draft.sentRev, rev);
            const current = draftRef.current;
            if (current && current.id === draft.id) current.sentRev = Math.max(current.sentRev, rev);
          } else if (inFlightRevRef.current === rev) {
            // Not delivered: the next exit tries again.
            inFlightRevRef.current = 0;
          }
          return result;
        });
    },
    [trackForm]
  );

  // Leaving = the page goes to the background or away (on a phone there's no
  // later reliable moment: a background tab can be killed without notice), or
  // the form unmounts with its host. Closing the modal is wired via
  // `leaveFormRef` above.
  useEffect(() => {
    leaveFormRef.current = () => sendPartial();
    if (typeof document === 'undefined') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') sendPartial(true);
    };
    const onPageHide = () => sendPartial(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      leaveFormRef.current = null;
      sendPartial();
    };
  }, [sendPartial]);

  // "Don't know yet" ticked → the control is cleared and disabled (so `required`
  // no longer applies); unticked → it comes back empty / on its default.
  const setUnknown = (field, unknown) => {
    setUnknownKeys((prev) => ({ ...prev, [field.key]: unknown }));
    setCustomValues((prev) => ({ ...prev, [field.key]: initialCustomValue(field) }));
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleCustom = (name, value) => {
    setCustomValues((prev) => ({ ...prev, [name]: value }));
  };

  // #1200 item 5 — append a suggestion pill's text to a field's current value
  // (space-separated; the user-chosen "append" behavior). Works for core + custom.
  const appendSuggestion = (field, text) => {
    const { key } = field;
    const isCore = CORE_INPUT_KEYS.includes(key);
    const current = (isCore ? formData[key] : customValues[key]) || '';
    const sep = current.trim().length > 0 && !/\s$/.test(current) ? ' ' : '';
    const next = `${current}${sep}${text}`;
    if (isCore) setFormData((prev) => ({ ...prev, [key]: next }));
    else handleCustom(key, next);
  };

  // #1200 item 3 — set a phone field's value and clear any prior validation error
  // for that key as the visitor edits it.
  const setPhoneValue = (field, value) => {
    const { key } = field;
    setPhoneErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    // Also drop the top-level 'phone' banner as soon as the visitor edits the
    // number — otherwise it lingers (asserting the number is invalid) until the
    // next submit even after they've corrected it.
    setFormError((prev) => (prev === 'phone' ? null : prev));
    if (CORE_INPUT_KEYS.includes(key)) setFormData((prev) => ({ ...prev, [key]: value }));
    else handleCustom(key, value);
  };

  const startChatMorph = useCallback(
    (lead) => {
      // Freeze the FULL launchContext and hand it to the inline chat, which
      // forwards it VERBATIM as the auto-greeting (preserving top-level
      // `leadAlreadyCaptured`). Arming the watchdog here (an event callback, not
      // an effect) keeps it exactly-once under React StrictMode.
      setLaunchContext(
        buildLaunchContext({ lead, cfg, estimatedMonthly: lead.estimatedMonthly })
      );
      // sg_form_handover_start — morph → chat begins. Fires per attempt (a
      // degraded "try again" re-morph is a genuine new handover). Stash the lead
      // id so the outcome events (complete / degraded) can reference it.
      activeLeadIdRef.current = lead.leadId || null;
      trackForm('sg_form_handover_start', { leadId: lead.leadId || undefined });
      armReplyWatchdog();
      setPhase('chat');
    },
    [cfg, armReplyWatchdog, trackForm]
  );

  // #1200 item 3 — filled-but-invalid phone numbers among `fields`. Empty+required
  // is caught natively by the national input's `required` attr, so this only
  // guards a too-short / malformed number the visitor actually typed.
  const findPhoneErrors = (fields) => {
    const errors = {};
    for (const f of fields) {
      if (f.enabled === false || f.type !== 'tel') continue;
      const v = CORE_INPUT_KEYS.includes(f.key) ? formData[f.key] : customValues[f.key];
      if (v && !isValidPhoneValue(v)) errors[f.key] = true;
    }
    return errors;
  };

  // The lead as the CRM gets it. #1469: `upToStep` keeps only the fields on
  // screens up to that step — what a partial lead carries.
  const buildLead = (upToStep = null) => {
    const onFinishedScreen = (f) => upToStep == null || (f.step || 1) <= upToStep;
    const coreValue = (key) => {
      const field = cfg.fieldList.find((f) => f.key === key);
      return field && !onFinishedScreen(field) ? '' : formData[key];
    };

    // Include the preferred channel only when an enabled channel field is
    // rendered with at least one offered channel (#1179 — the channel field's
    // presence, not a separate flag, decides this).
    const hasChannelField =
      cfg.fieldList.some(
        (f) => isChannelField(f) && f.enabled !== false && onFinishedScreen(f)
      ) && cfg.preferredChannels.length > 0;
    const preferredChannel = hasChannelField
      ? mapPreferredChannel(formData.preferredChannel)
      : undefined;

    // Build the lead `extra` (backend trims to 4KB). Start from caller-supplied
    // non-rendered metadata (#1087 — e.g. the car page's carPrice / deposit /
    // financeAmount), then layer config-declared custom-field inputs on top.
    //
    // #1231 — key custom-field values by their human LABEL, not the slug key, so
    // the CRM lead detail reads "Pick-up Date: 2026-07-30" instead of the opaque
    // "custom_6: 2026-07-30". The label travels with the lead (snapshot), so it's
    // stable even if the template is later edited. `extraData` (finance figures
    // like financeAmount) keeps its own keys — the finance calc below and the
    // verbatim/generic MODX post read those by key, and those forms are unaffected.
    const extra = { ...(cfg.extraData || {}) };
    for (const f of customFieldList) {
      // Hidden fields never reach the lead — a slider has a value even unrendered.
      if (f.enabled === false || !onFinishedScreen(f)) continue;
      let v = customValues[f.key];
      // #1469 — "Don't know yet" is sent as its label; a slider with its unit.
      if (canBeUnknown(f) && unknownKeys[f.key]) v = unknownLabelOf(f);
      else if (f.type === 'range' && v !== undefined && v !== '') v = formatRangeValue(f, v);
      if (v === undefined || v === '') continue;
      const label = typeof f.label === 'string' && f.label.trim() ? f.label.trim() : humanizeKey(f.key);
      extra[label] = v;
    }

    // Finance forms: compute the indicative monthly with the DEALER-configured
    // balloon-loan calc (#1087 bug fix — was bare default terms). The financed
    // amount arrives via `extra.financeAmount` (the caller's real figure); we
    // only estimate when we actually have one.
    let estimatedMonthly;
    if (cfg.formType === 'finance' && extra.financeAmount != null) {
      const amount = Number(extra.financeAmount);
      if (Number.isFinite(amount) && amount > 0) {
        estimatedMonthly = calculateMonthlyPayment(amount);
        if (estimatedMonthly > 0) {
          extra.monthlyPayment = estimatedMonthly;
          extra.estimatedMonthly = estimatedMonthly;
        }
      }
    }

    const leadPayload = {
      name: coreValue('name'),
      phone: coreValue('phone') || undefined,
      email: coreValue('email') || undefined,
      formType: cfg.formType,
      preferredChannel,
      honeypot: '',
      carOfferId: cfg.carId || undefined,
      carDescription: cfg.carTitle || undefined,
      vehicleInterest: cfg.carTitle || undefined,
      sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      message: coreValue('message') || undefined,
      sessionId: getCurrentSessionId(),
      visitorId: getCurrentVisitorId(),
      estimatedMonthly,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
      // #1440 — omitted when nothing is ticked (not ticking is not a withdrawal).
      consents: buildLeadConsents(consent, consentLocale),
    };

    return { leadPayload, extra, estimatedMonthly, preferredChannel };
  };

  // #1469 — the draft this submission continues: only one with the same phone
  // or email. On a shared device the next person may reopen the form (or find
  // it still open) and type their own details — they get a lead of their own,
  // never the first visitor's. A visitor who changes both after their partial
  // went out gets a new one too: a second lead beats merging two people.
  const draftFor = (payload) => {
    const prev = draftRef.current;
    return prev && sameContact(prev.payload, payload) ? prev : null;
  };

  // #1469 — a screen is done: record what's finished so far. Not before
  // there's a phone or email — a partial lead without one is nothing to call
  // back.
  const rememberProgress = (completedScreens) => {
    if (formData.honeypot) return;
    const { leadPayload } = buildLead(steps[completedScreens - 1]);
    if (!leadPayload.phone && !leadPayload.email) return;
    const prev = draftFor(leadPayload);
    const id = prev ? prev.id : newDraftId();
    const draft = {
      id,
      rev: (prev ? prev.rev : 0) + 1,
      sentRev: prev ? prev.sentRev : 0,
      payload: {
        ...leadPayload,
        progress: { draftId: id, completedSteps: completedScreens, totalSteps: steps.length },
      },
    };
    draftRef.current = draft;
    setLeadFormDraft(draftKey, draft);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // #1440 — the request consent is required, to move on as well as to send.
    // The browser already stops the submit while its box is on screen (the
    // first screen of a walk-through); this covers the later screens, which
    // don't show it.
    if (!consent.request) {
      if (walkthrough && safeStepIndex > 0) goToStep(0);
      return;
    }

    // #1469 — walk-through, not on the last screen yet: this submit is "Next".
    // The browser already validated this screen's controls (the only ones
    // rendered) before firing submit; phone format is ours to check.
    if (walkthrough && !isLastStep) {
      const stepPhoneErrors = findPhoneErrors(
        cfg.fieldList.filter((f) => (f.step || 1) === currentStep)
      );
      if (Object.keys(stepPhoneErrors).length > 0) {
        setPhoneErrors(stepPhoneErrors);
        setFormError('phone');
        return;
      }
      rememberProgress(safeStepIndex + 1);
      goToStep(safeStepIndex + 1);
      return;
    }

    // Honeypot: a filled hidden field means a bot. Silently drop — no CRM, no
    // MODX, no morph — and show a neutral success so the bot gets no signal.
    if (formData.honeypot && formData.honeypot.trim().length > 0) {
      console.debug('[LeadFormEmbed] Honeypot filled — silent drop');
      setSavedLead({
        name: formData.name,
        formType: cfg.formType,
        preferredChannel: undefined,
      });
      setPhase('degraded');
      return;
    }

    // #1200 item 3 — validate filled-but-invalid phone numbers across the WHOLE
    // form (in a walk-through the number may sit on an earlier screen).
    const nextPhoneErrors = findPhoneErrors(cfg.fieldList);
    if (Object.keys(nextPhoneErrors).length > 0) {
      setPhoneErrors(nextPhoneErrors);
      if (walkthrough) {
        // Take the visitor back to the screen with the bad number.
        const bad = cfg.fieldList.find((f) => nextPhoneErrors[f.key]);
        const badIndex = steps.indexOf((bad && bad.step) || 1);
        if (badIndex >= 0 && badIndex !== safeStepIndex) goToStep(badIndex);
      }
      setFormError('phone');
      return;
    }

    // sg_form_submit — a real (non-bot) submit was pressed. Fired BEFORE the
    // capture POST (like #1116) so it measures intent independent of server-side
    // validation/dedup; the resulting leadId is carried by sg_form_handover_*.
    trackForm('sg_form_submit');

    setPhase('saving');

    // #1469 — the full form completes the partial lead, if one went out: let
    // an in-flight partial land first, and send no more of them.
    finishingRef.current = true;
    if (partialSendRef.current) await partialSendRef.current;

    const { leadPayload, extra, estimatedMonthly, preferredChannel } = buildLead();
    const continued = draftFor(leadPayload);
    if (continued) {
      leadPayload.progress = {
        draftId: continued.id,
        completedSteps: steps.length,
        totalSteps: steps.length,
      };
    }

    // CRM-capture FIRST — success is gated on this, not on the MODX POST.
    const result = await submitLeadFromForm(leadPayload);
    if (!result) {
      // submitLeadFromForm swallows all failures and returns null. The most
      // common (silent, config-level) reason is that `captureFormsToCrm` is OFF
      // — a SEPARATE flag from enabling the Form Widget. Without CRM capture the
      // form has no persistence path at all, so the lead is genuinely lost.
      // Call it out specifically (defense-in-depth for the b2b-ui coupling in
      // #1081) so the misconfiguration is visible in the console, not a generic
      // "something went wrong". Runtime + network failures fall through to the
      // generic retry copy.
      if (!legacyConfig.CAPTURE_FORMS_TO_CRM) {
        console.error(
          '[LeadFormEmbed] captureFormsToCrm disabled — lead not saved. ' +
            'Enable "Capture Forms to CRM" alongside the Form Widget in widget config.'
        );
        setFormError('capture-disabled');
      } else {
        // No token / no API URL / network / non-2xx — a transient or wiring
        // failure the visitor can retry.
        console.error('[LeadFormEmbed] lead capture returned null — lead not saved');
        setFormError('capture');
      }
      // Not sent: leaving now should still send the finished screens (#1469).
      finishingRef.current = false;
      setPhase('form');
      return;
    }

    // #1469 — the dealer has the whole enquiry; nothing left to resend.
    clearLeadFormDraft(draftKey);
    draftRef.current = null;

    const lead = {
      name: formData.name,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      message: formData.message || undefined,
      formType: cfg.formType,
      preferredChannel,
      leadId: result.leadId,
      estimatedMonthly,
    };
    setSavedLead(lead);

    // #1162 — successful capture ⇒ append the PPC conversion marker to the
    // CURRENT page URL (History API, no reload). This is the SAME success path
    // that fires #1121's success events; both the morph and degraded branches
    // below follow a successful capture, so fire once here before either. Guard
    // so it runs at most once per submit (honeypot / validation-fail /
    // null-capture returned earlier and never reach here). A repeat the backend
    // deduped (#995, `leadId: null`) is no new lead, so it is no conversion.
    if (result.leadId && !successMarkerFiredRef.current) {
      successMarkerFiredRef.current = true;
      // #1566 — the conversion as an event, once: dataLayer `lead_submitted` +
      // Meta pixel `Lead`. The URL marker stays only briefly, so a URL-based
      // GTM trigger also fires once.
      trackLeadSubmitted({ formType: cfg.formType, carId: cfg.carId, leadId: result.leadId });
      emitSuccessUrlMarker(cfg.successParam);
      const param = cfg.successParam;
      successMarkerClearRef.current = {
        param,
        timer: setTimeout(() => {
          successMarkerClearRef.current = null;
          clearSuccessUrlMarker(param);
        }, SUCCESS_MARKER_LIFETIME_MS),
      };
    }

    // Optional MODX FormIt POST — fire-and-forget, AFTER capture, never gating.
    //
    // If the caller supplied a verbatim `modxFields` map/builder (#1087 owner
    // decision — the car page hands Andys' EXACT legacy payload), send it AS-IS
    // and DO NOT layer the generic shape over it. Otherwise fall back to the
    // form-agnostic superset any FormIt can consume: a generic identity
    // (`formEnquire` = template label), the standard contact fields, FormIt
    // housekeeping (`nospam`/`url`), and the lead `extra` spread in.
    const sourceUrl = buildSourceUrl();
    let modxPayload;
    if (typeof cfg.modxFields === 'function') {
      modxPayload = cfg.modxFields({
        formData,
        extra,
        carTitle: cfg.carTitle || '',
        sourceUrl,
      });
    } else if (cfg.modxFields && typeof cfg.modxFields === 'object') {
      modxPayload = cfg.modxFields;
    } else {
      modxPayload = {
        formEnquire: cfg.label || cfg.title || undefined,
        name: formData.name,
        email: formData.email || '',
        phone: formData.phone || '',
        message: formData.message || '',
        position: cfg.carTitle || '',
        nospam: '',
        url: sourceUrl,
        ...extra,
      };
    }
    fireModxPost(cfg.modxEndpoint, modxPayload);

    if (cfg.morphToChat) {
      startChatMorph(lead);
    } else {
      setPhase('degraded');
    }
  };

  // ─── Appearance overrides (#1179) ─────────────────────────────────────────
  // accentColor → theme-primary CSS vars; cornerRadius → --sg-form-radius;
  // width (per presentation) → maxWidth. Omitted appearance inherits the widget
  // theme (the CSS var fallbacks in FormWidget.css).
  const appearance = cfg.appearance || {};
  const themeVars = {};
  // CTA / focus color (#1204): the template's accent override wins; otherwise
  // inherit the ORG's brand primary from the widget theme (was a hardcoded blue
  // default before). Only the primary tokens are set here — surgical, so the
  // form keeps its neutral surface/text and only the CTA/focus follow the brand.
  const brandPrimary =
    appearance.accentColor || (widgetTheme && widgetTheme.primaryColor) || null;
  if (brandPrimary) {
    // Derive a slightly darkened hover shade rather than reusing the accent
    // verbatim (review !1113 #3) — a same-color hover reads as "dead" on the
    // submit / composer buttons. `color-mix` handles any CSS color format
    // (hex/rgb/named) and matches how the rest of FormWidget.css shades.
    const hover = `color-mix(in srgb, ${brandPrimary} 85%, #000)`;
    themeVars['--theme-primary'] = brandPrimary;
    themeVars['--theme-primary-hover'] = hover;
    themeVars['--widget-primary'] = brandPrimary;
    themeVars['--widget-primary-hover'] = hover;
  }
  if (typeof appearance.cornerRadius === 'number') {
    themeVars['--sg-form-radius'] = `${appearance.cornerRadius}px`;
  }
  // #1200 item 2 — typography scale. Set only the *-desktop / *-mobile INPUTS;
  // the consumed `--sg-form-font-scale` is defined in FormWidget.css so the
  // mobile media query can override it (an inline value could not be overridden).
  if (typeof appearance.fontScale === 'number') {
    themeVars['--sg-form-font-scale-desktop'] = String(appearance.fontScale);
  }
  if (typeof appearance.mobileFontScale === 'number') {
    themeVars['--sg-form-font-scale-mobile'] = String(appearance.mobileFontScale);
  }
  // #1200 item 6 — per-side padding for the embed area (px). Unset sides fall
  // back to 0 via the CSS var default (no regression vs the pre-#1200 card).
  const pad = appearance.padding;
  if (pad && typeof pad === 'object') {
    if (typeof pad.top === 'number') themeVars['--sg-form-pad-top'] = `${pad.top}px`;
    if (typeof pad.right === 'number') themeVars['--sg-form-pad-right'] = `${pad.right}px`;
    if (typeof pad.bottom === 'number') themeVars['--sg-form-pad-bottom'] = `${pad.bottom}px`;
    if (typeof pad.left === 'number') themeVars['--sg-form-pad-left'] = `${pad.left}px`;
  }
  const widthPx = isModal ? appearance.widthModal : appearance.widthInline;
  // Inline: width caps the card. Modal: width caps the sheet (below) and the
  // inner card fills it.
  const contentStyle = { ...themeVars };
  if (typeof widthPx === 'number') {
    contentStyle.maxWidth = isModal ? '100%' : `${widthPx}px`;
  }
  const sheetStyle = { ...themeVars };
  if (isModal && typeof widthPx === 'number') sheetStyle.maxWidth = `${widthPx}px`;
  // #1227 — modal vertical position. A positive offset top-aligns the sheet
  // (clearing a host's sticky header) and caps its height so a tall form scrolls
  // inside rather than sliding behind the header. 0 = centered (CSS default).
  // Driven by CSS vars + a modifier class (NOT raw inline align/max-height) so
  // the mobile bottom-sheet rules in the stylesheet can still override it.
  const modalOffsetTop =
    isModal &&
    typeof appearance.modalOffsetTop === 'number' &&
    appearance.modalOffsetTop > 0
      ? appearance.modalOffsetTop
      : 0;
  const backdropStyle =
    modalOffsetTop > 0
      ? { '--sg-form-modal-offset-top': `${modalOffsetTop}px` }
      : undefined;
  const backdropClass =
    modalOffsetTop > 0
      ? 'sg-form-modal__backdrop sg-form-modal__backdrop--offset'
      : 'sg-form-modal__backdrop';
  if (modalOffsetTop > 0) {
    sheetStyle['--sg-form-modal-max-h'] = `calc(100dvh - ${modalOffsetTop + 16}px)`;
  }

  // #1230 — modal trigger ("open form" CTA) button sizing overrides, applied
  // inline (the button renders only in modal presentation, and inline styles
  // cross the Shadow DOM boundary). Width precedence: explicit `triggerWidth` >
  // `triggerFullWidth` (100%) > theme default (content-width). `triggerRadius`
  // overrides the button's border-radius (which otherwise follows cornerRadius).
  const triggerStyle = { ...themeVars };
  if (typeof appearance.triggerWidth === 'number' && appearance.triggerWidth > 0) {
    triggerStyle.width = `${appearance.triggerWidth}px`;
  } else if (appearance.triggerFullWidth === true) {
    triggerStyle.width = '100%';
  }
  if (typeof appearance.triggerHeight === 'number' && appearance.triggerHeight > 0) {
    triggerStyle.height = `${appearance.triggerHeight}px`;
  }
  if (typeof appearance.triggerRadius === 'number' && appearance.triggerRadius >= 0) {
    triggerStyle.borderRadius = `${appearance.triggerRadius}px`;
  }

  // ─── One field of the unified ordered list (#1179 + #1087 labels) ─────────
  // EVERY field type — text/number/email/tel/date/select/radio/textarea and the
  // channel chooser — renders a consistent, persistent top `<label>` above the
  // control (owner-flagged: three inconsistent behaviors before — placeholder
  // for text, nothing for date, bespoke span for radio/select). The required
  // `*` lives on the LABEL; placeholders are demoted to optional hints and no
  // longer carry the field name. Core keys keep their `name` attr (formData
  // binding); customs bind via handleCustom → collected into `extra` on submit.
  const fieldLabelText = (field) =>
    field.label || CORE_FIELD_LABELS[field.key] || humanizeKey(field.key);

  // #1469 — heading of walk-through screen `step` (`stepTitles[0]` = step 1).
  const stepTitleOf = (step) => {
    const titles = cfg.appearance && cfg.appearance.stepTitles;
    const t = Array.isArray(titles) ? titles[step - 1] : null;
    return typeof t === 'string' && t.trim() ? t.trim() : null;
  };

  const renderField = (field) => {
    if (field.enabled === false) return null;
    const { key } = field;
    const required = !!field.required;
    const labelText = fieldLabelText(field);
    const fieldId = `sg-field-${key}`;
    // #1227 — grid column width. 'half' pairs two-per-row; default spans the row.
    const wrapperClass =
      field.width === 'half'
        ? 'sg-form-widget__field sg-form-widget__field--half'
        : 'sg-form-widget__field';

    // Persistent top label. `htmlFor` associates it with a single control; for
    // radio/channel GROUPS we instead use aria-labelledby (below) since a group
    // has no single labelable control.
    const RequiredMark = required ? (
      <span className="sg-form-widget__label-required" aria-hidden="true">
        {' *'}
      </span>
    ) : null;

    // ── Radio-like groups (channel chooser + custom radio) ──────────────────
    const renderRadioGroup = (options, name, selectedValue, onSelect) => {
      const labelId = `${fieldId}-label`;
      return (
        <div key={key} className={wrapperClass}>
          <span id={labelId} className="sg-form-widget__label">
            {labelText}
            {RequiredMark}
          </span>
          <div
            className="sg-form-widget__channels-options"
            role="group"
            aria-labelledby={labelId}
          >
            {options.map((opt) => (
              <label key={opt.value} className="sg-form-widget__channel">
                <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  checked={selectedValue === opt.value}
                  onChange={() => onSelect(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      );
    };

    if (isChannelField(field)) {
      if (cfg.preferredChannels.length === 0) return null;
      // Подписи вариантов можно передать в `field.options` ({value, label}) —
      // так витрина даёт их по языку (#1335). Без них — прежние английские.
      const customLabel = (value) =>
        Array.isArray(field.options)
          ? field.options.find((o) => o && o.value === value)?.label
          : undefined;
      const options = cfg.preferredChannels.map((ch) => {
        const value = ch === 'phone' ? 'call' : ch;
        return {
          value,
          label:
            customLabel(value) ??
            (ch === 'phone' ? 'Call' : ch === 'whatsapp' ? 'WhatsApp' : 'Email'),
        };
      });
      return renderRadioGroup(
        options,
        'preferredChannel',
        formData.preferredChannel,
        (v) => setFormData((prev) => ({ ...prev, preferredChannel: v }))
      );
    }

    if (field.type === 'radio') {
      const options = (field.options || []).map((opt) => ({ value: opt, label: opt }));
      return renderRadioGroup(options, `radio-${key}`, customValues[key] || '', (v) =>
        handleCustom(key, v)
      );
    }

    // ── Single-control fields (input / textarea / select / range) ───────────
    const isCore = CORE_INPUT_KEYS.includes(key);
    const value = isCore ? formData[key] : customValues[key] || '';
    const onChange = isCore ? handleField : (e) => handleCustom(key, e.target.value);
    const nameAttr = isCore ? key : undefined;
    // #1469 — "Don't know yet": the ticked control is disabled, which also lifts
    // `required` (disabled controls are skipped by constraint validation).
    const offersUnknown = canBeUnknown(field);
    const isUnknown = offersUnknown && !!unknownKeys[key];
    const controlRequired = required && !isUnknown;
    // #1200 item 4 — restore light inline hints. An explicit per-field placeholder
    // wins; core fields fall back to a sensible default sample. `??` (not `||`) so
    // an intentional empty string could suppress the hint (designer trims empties
    // to undefined, so in practice this resolves to the default for core fields).
    const placeholder = field.placeholder ?? CORE_FIELD_PLACEHOLDERS[key] ?? undefined;

    // #1200 item 5 — autosuggest pills under the control (most useful on message).
    const suggestionsNode =
      Array.isArray(field.suggestions) && field.suggestions.length > 0 ? (
        <div className="sg-form-widget__field-suggestions">
          <Suggestions
            suggestions={field.suggestions}
            onSelect={(text) => appendSuggestion(field, text)}
            maxVisible={field.suggestions.length}
            accentColor={cfg.appearance?.accentColor}
          />
        </div>
      ) : null;

    let control;
    if (field.type === 'range') {
      // #1469 — slider (e.g. lease period 1–24 months). The current value is
      // printed beside it and read out via aria-valuetext with its unit.
      const rc = rangeConfig(field);
      const current = value === '' ? rc.initial : Number(value);
      const valueText = formatRangeValue(field, current);
      const pct = ((current - rc.min) / (rc.max - rc.min)) * 100;
      control = (
        <div className="sg-form-widget__range">
          <input
            id={fieldId}
            type="range"
            className="sg-form-widget__range-input"
            min={rc.min}
            max={rc.max}
            step={rc.increment}
            value={current}
            onChange={onChange}
            disabled={isUnknown}
            aria-valuetext={valueText}
            style={{ '--sg-range-pct': `${pct}%` }}
          />
          <output className="sg-form-widget__range-value" htmlFor={fieldId} aria-hidden="true">
            {isUnknown ? '—' : valueText}
          </output>
        </div>
      );
    } else if (field.type === 'tel') {
      // #1200 item 3 — country dial-code picker + digit-only national input.
      control = (
        <PhoneField
          id={fieldId}
          value={value}
          onChange={(v) => setPhoneValue(field, v)}
          required={required}
          defaultCountry={cfg.appearance?.defaultCountry}
          placeholder={placeholder}
          invalid={!!phoneErrors[key]}
        />
      );
    } else if (field.type === 'textarea') {
      control = (
        <textarea
          id={fieldId}
          name={nameAttr}
          className="sg-form-widget__input sg-form-widget__textarea"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={controlRequired}
          disabled={isUnknown}
          rows={3}
        />
      );
    } else if (field.type === 'select') {
      control = (
        <select
          id={fieldId}
          name={nameAttr}
          className="sg-form-widget__input"
          value={value}
          required={controlRequired}
          disabled={isUnknown}
          onChange={onChange}
        >
          <option value="">{`Select ${labelText.toLowerCase()}…`}</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    } else {
      const inputType =
        field.type === 'number'
          ? 'number'
          : field.type === 'date'
            ? 'date'
            : field.type === 'email'
              ? 'email'
              : field.type === 'tel'
                ? 'tel'
                : 'text';
      const autoComplete =
        key === 'name' ? 'name' : key === 'email' ? 'email' : key === 'phone' ? 'tel' : undefined;
      control = (
        <input
          id={fieldId}
          type={inputType}
          name={nameAttr}
          className="sg-form-widget__input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={controlRequired}
          disabled={isUnknown}
          autoComplete={autoComplete}
        />
      );
    }

    return (
      <div key={key} className={wrapperClass}>
        <label className="sg-form-widget__label" htmlFor={fieldId}>
          {labelText}
          {RequiredMark}
        </label>
        {control}
        {offersUnknown && (
          <label className="sg-form-widget__unknown">
            <input
              type="checkbox"
              checked={isUnknown}
              onChange={(e) => setUnknown(field, e.target.checked)}
            />
            {unknownLabelOf(field)}
          </label>
        )}
        {suggestionsNode}
      </div>
    );
  };

  // ─── Phase bodies ─────────────────────────────────────────────────────────
  let body;
  if (phase === 'chat') {
    // The form card morphs IN PLACE into a lightweight INLINE chat (#1158) — an
    // in-flow view inside this same Shadow-DOM region, NOT the floating
    // VoiceWidgetConnected overlay. FormWidgetChat drives the conversation via
    // the standalone useTextChat transport and reports the agent's own first
    // reply back through `onReplyStarted` (messageId-correlated → F4 fix).
    body = (
      <div style={themeVars}>
        <FormWidgetChat
          lead={savedLead}
          launchContext={launchContext}
          theme={widgetTheme}
          successBanner={cfg.successBanner}
          onReplyStarted={handleReplyStarted}
        />
      </div>
    );
  } else if (phase === 'degraded') {
    const ch = savedLead?.preferredChannel;
    body = (
      <div className="sg-form-widget sg-form-widget--done" style={contentStyle}>
        <CheckCircle className="sg-form-widget__done-icon" aria-hidden="true" />
        <p className="sg-form-widget__done-title">Thank you{savedLead?.name ? `, ${savedLead.name}` : ''}!</p>
        <p className="sg-form-widget__done-text">
          We&apos;ll reach you {channelLabel(ch)}.
        </p>
        {cfg.morphToChat && (
          <button
            type="button"
            className="sg-form-widget__retry"
            onClick={() => savedLead && startChatMorph(savedLead)}
          >
            <MessageSquare className="sg-form-widget__retry-icon" aria-hidden="true" />
            {cfg.handoverCtaText}
          </button>
        )}
      </div>
    );
  } else {
    const isSaving = phase === 'saving';
    body = (
      <div className="sg-form-widget" style={contentStyle}>
        {/* #1200 item 1 — the host page often already shows a heading above an
            inline embed, so `hideTitle` suppresses the form's own duplicate. */}
        {cfg.title && !(cfg.appearance && cfg.appearance.hideTitle) && (
          <h3 className="sg-form-widget__title">{cfg.title}</h3>
        )}
        {formError === 'capture' && (
          <p className="sg-form-widget__error" role="alert">
            Something went wrong sending your request. Please try again.
          </p>
        )}
        {formError === 'phone' && (
          <p className="sg-form-widget__error" role="alert">
            Please enter a valid phone number.
          </p>
        )}
        {formError === 'capture-disabled' && (
          <p className="sg-form-widget__error" role="alert">
            This form isn&apos;t able to accept submissions right now. Please contact us
            directly and we&apos;ll be glad to help.
          </p>
        )}
        {walkthrough && (
          <div className="sg-form-widget__steps">
            <div className="sg-form-widget__steps-bar" aria-hidden="true">
              {steps.map((st, i) => (
                <span
                  key={st}
                  className={
                    i <= safeStepIndex
                      ? 'sg-form-widget__steps-seg sg-form-widget__steps-seg--done'
                      : 'sg-form-widget__steps-seg'
                  }
                />
              ))}
            </div>
            {/* Focus lands here on each screen change, so a screen reader reads
                the new step and the sheet scrolls back to its top (#1469). */}
            <p ref={stepTitleRef} tabIndex={-1} className="sg-form-widget__step-title">
              <span className="sg-form-widget__step-count">
                {`Step ${safeStepIndex + 1} of ${steps.length}`}
              </span>
              {stepTitleOf(currentStep) && (
                <span className="sg-form-widget__step-name">{stepTitleOf(currentStep)}</span>
              )}
            </p>
          </div>
        )}
        <form id={formId} className="sg-form-widget__form" onSubmit={handleSubmit}>
          {/* Unified ordered fields (#1179): core + custom interleaved, in order.
              Walk-through (#1469): only the current screen's fields. */}
          {(walkthrough
            ? cfg.fieldList.filter((field) => (field.step || 1) === currentStep)
            : cfg.fieldList
          ).map((field) => renderField(field))}

          {/* #1440 — contact consent, on every form type, in two boxes. The
              required request box opens the form (a walk-through's first
              screen), so every partial lead (#1469) carries it; the optional
              offers box closes it. */}
          {onFirstScreen && (
            <ConsentCheckbox
              box="request"
              id={`${formId}-consent-request`}
              checked={consent.request}
              onCheckedChange={(checked) => setConsent((c) => ({ ...c, request: checked }))}
              dealerName={legacyConfig.CONSENT_DEALER_NAME}
              privacyUrl={legacyConfig.DEALER_PRIVACY_URL}
              locale={consentLocale}
              disabled={isSaving}
            />
          )}
          {isLastStep && (
            <ConsentCheckbox
              box="marketing"
              id={`${formId}-consent-marketing`}
              checked={consent.marketing}
              onCheckedChange={(checked) => setConsent((c) => ({ ...c, marketing: checked }))}
              dealerName={legacyConfig.CONSENT_DEALER_NAME}
              // One privacy link per screen.
              privacyUrl={onFirstScreen ? null : legacyConfig.DEALER_PRIVACY_URL}
              locale={consentLocale}
              disabled={isSaving}
            />
          )}
          {/* #1469 — from this screen on, leaving still reaches the dealer. */}
          {walkthrough && onContactScreen && !isLastStep && (
            <p className="sg-form-widget__save-note">
              We&apos;ll save your details so our team can get back to you.
            </p>
          )}

          {/* Honeypot — hidden from real users; bots fill it → silent drop. */}
          <input
            type="text"
            name="honeypot"
            className="sg-form-widget__honeypot"
            value={formData.honeypot}
            onChange={handleField}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

        </form>
        {/* Футер ВНЕ `<form>` (#1270, ревью). Модалка закрепляет его снизу и
            уводит вниз через `margin-top: auto` — а ни то, ни другое не сделать,
            пока он лежит в форме: форма это grid, где auto-margin не толкает
            элемент к низу, а перевод её на flex ломает поля в полширины
            (`width: 'half'`, #1227). Обёртка прямоугольная, потому что кнопка —
            капсула: её габариты прозрачны по углам, и проезжающий контент
            просвечивал вокруг. Связь с формой — атрибутом `form`, поэтому
            implicit submit по Enter сохраняется. В inline-подаче футер стилей не
            несёт — обычный div. */}
        <div
          className={
            walkthrough
              ? 'sg-form-widget__actions sg-form-widget__actions--steps'
              : 'sg-form-widget__actions'
          }
        >
          {walkthrough && safeStepIndex > 0 && (
            <button
              type="button"
              className="sg-form-widget__back"
              onClick={() => goToStep(safeStepIndex - 1)}
              disabled={isSaving}
            >
              Back
            </button>
          )}
          {/* #1469 — still `type=submit` on earlier screens: the browser validates
              the visible step first, and Enter in a field moves on too. */}
          <button
            type="submit"
            form={formId}
            className="sg-form-widget__submit"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="sg-form-widget__spinner" aria-hidden="true" />
                Sending…
              </>
            ) : isLastStep ? (
              cfg.submitLabel
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>
    );
  }

  // ─── Presentation: inline vs modal (#1179) ────────────────────────────────
  // Modal renders `body` in a centered overlay (mobile = full-height bottom-
  // sheet, via CSS). A close control collapses it to a re-open button so the
  // form is never lost. Inline returns the body as-is (unchanged embed path).
  if (!isModal) return body;
  // The open sheet is portaled to the body-level root (#1232); the closed-state
  // trigger stays in-flow in this embed's shadow (it IS the page CTA).
  const overlay = (
    <div className={backdropClass} style={backdropStyle}>
      <div
        ref={sheetRef}
        className="sg-form-modal__sheet"
        style={sheetStyle}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {/* #1261 — крестик живёт в НЕСКРОЛЛЯЩЕЙСЯ шапке, а прокручивается только
            `__body`. Раньше он был `position: sticky` ВНУТРИ скролл-контейнера и
            пиннился лишь по вертикали, поэтому уезжал вместе с содержимым:
            замер на живой модалке — сдвиг −126px. Одного `overflow-x: hidden`
            мало, контейнер остаётся программно скроллящимся, а на iOS Safari
            сам подкручивает его к каретке при фокусе поля. Вне скролла крестик
            сдвинуть уже нечем. */}
        <div className="sg-form-modal__head">
          <button
            type="button"
            className="sg-form-modal__close"
            aria-label="Close"
            onClick={closeModal}
          >
            ×
          </button>
        </div>
        <div className="sg-form-modal__body">{body}</div>
      </div>
    </div>
  );
  let modalContent;
  if (modalOpen) {
    // Resolve (and lazily create) the body-level portal root only now, on open.
    const portalRoot = resolveModalPortalRoot();
    modalContent = portalRoot ? createPortal(overlay, portalRoot) : overlay;
  } else {
    modalContent = (
      <button
        type="button"
        className="sg-form-modal__open"
        style={triggerStyle}
        onClick={() => setModalOpen(true)}
      >
        {cfg.ctaLabel || cfg.title || 'Open form'}
      </button>
    );
  }
  return <div className="sg-form-modal">{modalContent}</div>;
}

LeadFormEmbedInner.propTypes = {
  cfg: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

/**
 * LeadFormEmbed — default export for lazy-loading in bootstrap COMPONENT_LOADERS.
 *
 * Provider tree: ContactParams → DutyFree → Notification. (The inline chat morph
 * uses the standalone `useTextChat` transport directly, so the VoiceAssistant
 * context / floating widget are no longer part of this tree — #1158.)
 */
export default function LeadFormEmbed({ attrs, onClose }) {
  const cfg = useMemo(() => resolveFormConfig(attrs), [attrs]);
  return (
    <ContactParamsProvider>
      <DutyFreeProvider>
        <NotificationProvider>
          <LeadFormEmbedInner cfg={cfg} onClose={onClose} />
        </NotificationProvider>
      </DutyFreeProvider>
    </ContactParamsProvider>
  );
}

LeadFormEmbed.propTypes = {
  attrs: PropTypes.object,
  // Optional: notified when a MODAL-presentation form is dismissed. A host that
  // owns its own trigger (the car page) uses this to unmount / reset its state.
  onClose: PropTypes.func,
};
